import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Store, Users, Package, RefreshCw, CheckCircle, 
  ArrowRight, Sparkles, Loader2, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const ONBOARDING_STEPS = [
  {
    id: 'company_setup',
    title: 'Company Details',
    description: 'Your store information has been imported from Shopify',
    icon: Store,
    action: null,
    autoComplete: true
  },
  {
    id: 'shopify_connected',
    title: 'Shopify Connected',
    description: 'Your Shopify store is linked to Opswarex',
    icon: Package,
    action: null,
    autoComplete: true
  },
  {
    id: 'first_sync',
    title: 'Sync Products & Orders',
    description: 'Import your products and orders from Shopify',
    icon: RefreshCw,
    action: 'sync',
    actionLabel: 'Sync Now'
  },
  {
    id: 'team_invited',
    title: 'Invite Team Members',
    description: 'Add your team to help manage operations',
    icon: Users,
    action: 'invite',
    actionLabel: 'Invite Team',
    optional: true
  }
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [steps, setSteps] = useState({});
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/shopify-billing/onboarding-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSteps(response.data.steps);
      setProgress(response.data.progress.percentage);

      // Find current step
      const stepIndex = ONBOARDING_STEPS.findIndex(
        step => !response.data.steps[step.id]
      );
      setCurrentStep(stepIndex === -1 ? ONBOARDING_STEPS.length - 1 : stepIndex);

      if (response.data.completed) {
        // Already completed, redirect to dashboard
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Failed to fetch onboarding status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      
      // Sync products
      await axios.post(
        `${API_BASE_URL}/shopify/sync/products`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Sync orders
      await axios.post(
        `${API_BASE_URL}/shopify/sync/orders`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh status
      await fetchOnboardingStatus();
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleComplete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/shopify-billing/complete-onboarding`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 mb-6">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">
            Welcome to Opswarex!
          </h1>
          <p className="text-gray-300 text-lg">
            Let's get your operations center ready in just a few steps
          </p>
        </div>

        {/* Progress */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 mb-8 border border-white/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-300">Setup Progress</span>
            <span className="text-sm font-medium text-white">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Steps */}
        <div className="space-y-4">
          {ONBOARDING_STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isCompleted = steps[step.id];
            const isCurrent = index === currentStep;
            const isPending = index > currentStep;

            return (
              <div
                key={step.id}
                className={`
                  bg-white/10 backdrop-blur-lg rounded-xl p-6 border transition-all
                  ${isCompleted 
                    ? 'border-green-500/50' 
                    : isCurrent 
                      ? 'border-purple-500 ring-2 ring-purple-500/30' 
                      : 'border-white/10'}
                `}
              >
                <div className="flex items-start gap-4">
                  <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center shrink-0
                    ${isCompleted 
                      ? 'bg-green-500' 
                      : isCurrent 
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                        : 'bg-white/10'}
                  `}>
                    {isCompleted ? (
                      <CheckCircle className="w-6 h-6 text-white" />
                    ) : (
                      <StepIcon className="w-6 h-6 text-white" />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-white">
                        {step.title}
                      </h3>
                      {step.optional && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-gray-300">
                          Optional
                        </span>
                      )}
                    </div>
                    <p className="text-gray-300 text-sm mb-3">
                      {step.description}
                    </p>

                    {/* Action Button */}
                    {!isCompleted && step.action && isCurrent && (
                      <div className="flex items-center gap-3">
                        {step.action === 'sync' && (
                          <Button
                            onClick={handleSync}
                            disabled={syncing}
                            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                          >
                            {syncing ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Syncing...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                {step.actionLabel}
                              </>
                            )}
                          </Button>
                        )}
                        {step.action === 'invite' && (
                          <Button
                            onClick={() => navigate('/users')}
                            variant="outline"
                            className="border-white/30 text-white hover:bg-white/10"
                          >
                            <Users className="w-4 h-4 mr-2" />
                            {step.actionLabel}
                            <ExternalLink className="w-3 h-3 ml-2" />
                          </Button>
                        )}
                        {step.optional && (
                          <Button
                            variant="ghost"
                            onClick={() => setCurrentStep(prev => prev + 1)}
                            className="text-gray-400 hover:text-white"
                          >
                            Skip for now
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Complete Button */}
        <div className="mt-8 text-center">
          <Button
            onClick={handleComplete}
            size="lg"
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-lg px-8"
          >
            Go to Dashboard
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-gray-400 text-sm mt-3">
            You can always complete these steps later from Settings
          </p>
        </div>
      </div>
    </div>
  );
}
